import {BrowserStorage} from "../storage/BrowserStorage";

declare global {
  interface Window {
    c_BlogCategories: string[] | undefined;
  }
}

const storageKey = "c_RedesignBlogNews";

export type BlogOverviewType = {
  headline: string;
  categoryName: string;
  text: string;
  image: string;
  link: string;
  author: string;
  authorImage: string;
  activationDate: string;
};

const browserStorage = new BrowserStorage<BlogOverviewType[]>({
  type: "localStorage",
  dataIdentifier: storageKey,
  hoursToCache: 12,
});

export async function loadBlogs(): Promise<BlogOverviewType[]> {
  const storageData = browserStorage.read("firstThree");
  if (storageData !== null) {
    return storageData;
  } else if (!window.c_BlogCategories) {
    return [];
  } else {
    // Gather all pending fetch promises, including async dom parser action and object creation
    const blogCategoriesPromises = window.c_BlogCategories.map((blogCategory) =>
      fetch(blogCategory)
        .then((resp) => resp.text())
        .then((domString): BlogOverviewType[] => {
          // parse dom and create json object (still async)
          const dom = new DOMParser().parseFromString(domString, "text/xml");
          const categoryName = dom.getElementsByTagName("CategoryName")[0]?.textContent?.trim() ?? "";
          const blogEntries = dom.getElementsByTagName("BlogEntry");

          return Array.from(blogEntries)
            .map((blogEntry): Partial<BlogOverviewType> | undefined => {
              try {
                return {
                  headline: blogEntry.getElementsByTagName("Headline")[0].textContent?.trim(),
                  categoryName,
                  text: blogEntry.getElementsByTagName("TextShort")[0].textContent?.trim(),
                  image: blogEntry.getElementsByTagName("SituationImage")[0].textContent?.trim(),
                  link: blogEntry.getElementsByTagName("Path")[0].textContent?.trim(),
                  author: blogEntry.getElementsByTagName("Author")[0]?.textContent?.trim() || "",
                  authorImage: blogEntry.getElementsByTagName("AuthorImage")[0]?.textContent?.trim() || "",
                  activationDate: blogEntry.getElementsByTagName("ActivationDate")[0].textContent?.trim(),
                };
              } catch (e) {
                // any field not set, so ignore whole data
                return undefined;
              }
            })
            .filter((entry): entry is BlogOverviewType => !!entry);
        })
        .catch(() => []),
    );

    // Wait for all to arrive and then proceed
    const blogOverviews = await Promise.all<Partial<BlogOverviewType>[]>(blogCategoriesPromises).then(
      (blogOverviewArrays: BlogOverviewType[][]) => {
        const sortFunction = (a: BlogOverviewType, b: BlogOverviewType) => {
          if (a.activationDate < b.activationDate) {
            return 1;
          } else if (a.activationDate > b.activationDate) {
            return -1;
          } else {
            return 0;
          }
        };
        const concatenation = ([] as BlogOverviewType[]).concat(...blogOverviewArrays);
        const concatenationSorted = concatenation.sort(sortFunction);

        return concatenationSorted.slice(0, 3);
      },
    );
    browserStorage.write("firstThree", blogOverviews);

    return blogOverviews;
  }
}
